import { Component, PanelItem, PanelItemGroup } from '@rta/rta-blueprint-library';

// Coyote's C1CHCS asado ships as its own catalog entry that predates the sleeveCompatible flag
// -- category is 'Smokers' and sleeveCompatible is false in the DB, so it needs an explicit
// exemption to be treated as sleeve-compatible. Same precedent as get-panel-sleeve.cfn.ts and
// countertop-spacing.ts.
const isSleeveWrapper = (component: Component): boolean => {
	return component.sleeveDepth !== null || (component.category?.isInsulatedJacket ?? false);
};

const isSleeveOccupant = (component: Component): boolean => {
	return component.sleeveCompatible || (component.category?.isGrillOrSideBurner ?? false) || component.sku === 'C1CHCS';
};

/**
 * Returns true when the incoming SKU pairs with the existing panelItem the way an asado /
 * grill nests inside a sleeve or insulated jacket. The AddAppliancePanel swap flow deletes
 * the existing item after adding the new one, which is wrong here -- both items belong in
 * the same PanelItemGroup so `getPanelSleeveCfn` can resolve the pairing on read.
 */
export const isSleevePairing = (existing: PanelItem | null, incoming: Component | null): boolean => {
	if (!existing || !incoming) return false;
	const existingComponent = existing.item?.component ?? null;
	if (!existingComponent) return false;

	return (
		(isSleeveWrapper(existingComponent) && isSleeveOccupant(incoming)) ||
		(isSleeveOccupant(existingComponent) && isSleeveWrapper(incoming))
	);
};

/**
 * Returns true when the group already contains an item that pairs with the incoming SKU as
 * sleeve wrapper + occupant. Used by resolvePanelItemGroup to keep an incoming asado in the
 * sleeve's group even though the asado's Smokers category defaults to TOP_ONLY placement
 * (which would otherwise force a fresh group and break the pairing).
 */
export const panelItemGroupHasSleevePairFor = (group: PanelItemGroup | null, incoming: Component | null): boolean => {
	if (!group || !incoming) return false;
	return group.items?.children.some((pi) => isSleevePairing(pi, incoming)) ?? false;
};
